/**
 * Copyright © 2025 Delicious AI, LLC
 *
 * @author Stockton Jenkins <stockton.jenkins@deliciousai.com>
 */

import { AutoAwesome, Insights } from '@mui/icons-material'
import { Stack, Typography } from '@mui/material'
import { Box, useTheme } from '@mui/system'
import { SparkLineChart } from '@mui/x-charts/SparkLineChart'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { RevenueOutlierMetadataFragment } from 'graphql/generated-types'
import Markdown from 'react-markdown'

import useT from '@/i18n/useT'
import { MarkdownHelpers } from '@/utils/markdown.helpers'

dayjs.extend(advancedFormat)

type RevenueOutlierInsightProps = {
  metadata: RevenueOutlierMetadataFragment
}

const RevenueOutlierInsight: React.FC<RevenueOutlierInsightProps> = ({
  metadata,
}) => {
  const theme = useTheme()
  const { t } = useT()
  const outlierIndices = metadata.relativeChangeIsOutlier
    .map((isOutlier, idx) => (isOutlier ? idx : null))
    .filter(isOutlier => isOutlier !== null)
  const outlierValues = metadata.relativeChangeValues.filter((_, idx) =>
    outlierIndices.includes(idx),
  )
  const sentiment = outlierValues.some(value => value < 0)
    ? t('aiInsightsTable.revenueOutlier.declineInRevenue')
    : t('aiInsightsTable.revenueOutlier.increaseInRevenue')
  const startDate = dayjs(metadata.relativeChangeDates[0]).format(
    'dddd, MMMM D',
  )
  const endDate = dayjs(
    metadata.relativeChangeDates[metadata.relativeChangeDates.length - 1],
  ).format('dddd, MMMM D')
  return (
    <>
      <Stack direction={'row'} alignItems={'center'} spacing={2}>
        <Insights />
        <Typography variant="h5">
          {t('aiInsightsTable.revenueOutlier.salesTrend', {
            startDate,
            endDate,
          })}
        </Typography>
      </Stack>
      <Box sx={{ flexGrow: 1 }}>
        <SparkLineChart
          data={metadata.relativeChangeValues}
          valueFormatter={value =>
            value !== null
              ? `${value.toLocaleString('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 1,
                })}%`
              : 'N/A'
          }
          xAxis={{
            scaleType: 'time',
            data: metadata.relativeChangeDates.map(date => dayjs(date)),
            valueFormatter: value => `Sales on ${value.format('dddd MMMM D')}`,
          }}
          height={200}
          curve="linear"
          color={theme.palette.primary.main}
          area
          showTooltip
          showHighlight
        />
      </Box>
      <Stack direction={'row'} alignItems={'center'} spacing={2}>
        <AutoAwesome />
        <Typography variant="h5">{sentiment}</Typography>
      </Stack>
      <Markdown>{MarkdownHelpers.format(metadata.message)}</Markdown>
    </>
  )
}

export default RevenueOutlierInsight
